import { useAtomValue, useSetAtom } from "jotai";
import { useParams } from "react-router-dom";
import { visitorNameAtom, activeConversationSidAtom } from "../../state";
import { useCreateVisitorChatExperienceCallback } from "./useCreateVisitorChatAccess";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useInitTwilioChatClient } from "../../../session/chat/useInitTwilioChatClient";
import { useUpdateVisitorPresenceCallback } from "../useUpdateVisitorPresenceCallback";
import { useVisitorId } from "../../useVisitorId";
import { ConnectionState, ConnectionStateStatus } from "../../../session";
import { memoize } from "../../../utils";

export function useInitVisitorChatAccess() {
  const { providerShortId } = useParams() as { providerShortId: string };

  const visitorId = useVisitorId();
  const name = useAtomValue(visitorNameAtom);
  const setActiveConversationSid = useSetAtom(activeConversationSidAtom);
  const [createVisitorChatExperience, { data }] =
    useCreateVisitorChatExperienceCallback();
  const updateVisitorPresence = useUpdateVisitorPresenceCallback();
  const conversationSidRef = useRef<string>();

  const handleChatInitSuccessMemoized = useMemo(() => {
    return memoize((conversationSid: string) => {
      setActiveConversationSid(conversationSid);
      updateVisitorPresence();
    });
  }, [setActiveConversationSid, updateVisitorPresence]);

  const handleConnectionStateChanged = useCallback(
    (newState: ConnectionStateStatus) => {
      if (newState === ConnectionState.CONNECTED) {
        if (conversationSidRef.current) {
          // Using memoized function to avoid unnecessary initialization calls
          handleChatInitSuccessMemoized(conversationSidRef.current);
        }
      }
    },
    [handleChatInitSuccessMemoized],
  );

  useEffect(() => {
    if (createVisitorChatExperience) {
      createVisitorChatExperience(providerShortId, name, visitorId).then(
        (result) => {
          conversationSidRef.current =
            result?.data?.createTwilioWaitingRoomConversation?.conversationSid;
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInitTwilioChatClient({
    twilioChatToken: data?.createTwilioVisitorChatAccess?.jwt,
    onConnectionStateChanged: handleConnectionStateChanged,
  });
}
